import React from 'react';
import { Users, Clock, Scissors, Calendar, MessageSquare } from 'lucide-react';

const ROLE_COLORS = {
  doctor: '#2563EB',
  technician: '#16A34A',
  consultant: '#9333EA',
  receptionist: '#6B7280',
  manager: '#D97706',
};

const STATUS_DOT = {
  scheduled: '#3b82f6',
  confirmed: '#16A34A',
  in_progress: '#EA580C',
  completed: '#059669',
  cancelled: '#DC2626',
  no_show: '#6B7280',
};

export default function ManagerStaffTimeline({ staffSchedule = [], isRTL, isMobile, t }) {
  if (!staffSchedule || staffSchedule.length === 0) return null;

  const sortedStaff = [...staffSchedule].sort((a, b) => b.meetingCount - a.meetingCount);
  const timeSlots = [];
  for (let h = 8; h <= 20; h++) {
    timeSlots.push(`${String(h).padStart(2, '0')}:00`);
  }

  const cardStyle = {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    padding: isMobile ? '1rem' : '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid var(--color-border)',
    marginBottom: '1rem',
  };

  return (
    <div style={cardStyle}>
      <h3 style={{
        fontSize: '0.9375rem', fontWeight: 600,
        color: 'var(--color-text-primary)',
        marginBottom: '0.75rem',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
      }}>
        <Users size={16} style={{ flexShrink: 0, width: 16, height: 16 }} />
        {t('managerDashboard.staffSchedule', "Today's Staff Schedule")}
        <span style={{
          marginInlineStart: '0.25rem', padding: '0.125rem 0.5rem', borderRadius: '9999px',
          backgroundColor: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontSize: '0.6875rem', fontWeight: 600,
        }}>
          {staffSchedule.filter(s => s.meetingCount > 0).length}/{staffSchedule.length} {t('managerDashboard.onDuty', 'on duty')}
        </span>
      </h3>

      <div style={{
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        direction: isRTL ? 'rtl' : 'ltr',
      }}>
        <div style={{ minWidth: isMobile ? '600px' : 'auto' }}>
          {/* Header row with time slots */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `140px repeat(${timeSlots.length}, 1fr)`,
            gap: 0, borderBottom: '1px solid var(--color-border)',
            paddingBottom: '0.375rem', marginBottom: '0.5rem',
          }}>
            <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-text-tertiary)' }}>
              {t('managerDashboard.worker', 'Worker')}
            </div>
            {timeSlots.map(slot => (
              <div key={slot} style={{
                fontSize: '0.6rem', color: 'var(--color-text-tertiary)',
                textAlign: 'center', fontVariantNumeric: 'tabular-nums',
              }}>
                {slot}
              </div>
            ))}
          </div>

          {/* Worker rows */}
          {sortedStaff.map(worker => (
            <div key={worker.workerId} style={{
              display: 'grid',
              gridTemplateColumns: `140px repeat(${timeSlots.length}, 1fr)`,
              gap: 0, padding: '0.375rem 0',
              borderBottom: '1px solid var(--color-border)',
              alignItems: 'center',
            }}>
              {/* Worker name + role */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', paddingInlineEnd: '0.5rem' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  backgroundColor: ROLE_COLORS[worker.workerRole] || '#6B7280',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.625rem', fontWeight: 700, flexShrink: 0,
                }}>
                  {(worker.workerName || '?')[0]}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {worker.workerName}
                  </div>
                  <div style={{ fontSize: '0.5625rem', color: ROLE_COLORS[worker.workerRole] || '#6B7280' }}>
                    {t(`workers.${worker.workerRole}`, worker.workerRole)}
                  </div>
                </div>
              </div>

              {/* Time slots with meeting blocks */}
              {timeSlots.map(slot => {
                const slotHour = parseInt(slot.split(':')[0]);
                const meetingsInSlot = worker.todayMeetings.filter(m => {
                  if (!m.time) return false;
                  const mHour = parseInt(m.time.split(':')[0]);
                  return mHour === slotHour;
                });

                return (
                  <div key={slot} style={{
                    height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {meetingsInSlot.length > 0 ? (
                      <div
                        title={meetingsInSlot.map(m => `${m.time} ${m.treatmentType || m.type}`).join(', ')}
                        style={{
                          width: '90%', height: '20px', borderRadius: '4px',
                          backgroundColor: meetingsInSlot[0].type === 'treatment' ? 'rgba(37,99,235,0.15)' : 'rgba(16,163,74,0.15)',
                          border: `1px solid ${meetingsInSlot[0].type === 'treatment' ? 'rgba(37,99,235,0.3)' : 'rgba(16,163,74,0.3)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.5625rem', fontWeight: 500,
                          color: meetingsInSlot[0].type === 'treatment' ? '#2563EB' : '#16A34A',
                          cursor: 'default',
                        }}
                      >
                        {meetingsInSlot.length > 1 ? meetingsInSlot.length : (meetingsInSlot[0].treatmentType || '').substring(0, 3)}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
